import React from "react";

import { JobService, ReferralService } from "@pro/_services";

import { Button } from "@common/components/Atoms/Button";
import Error from "@common/components/Atoms/Error";
import {
    FormHandler,
    Rules,
    Select,
} from "@common/components/Atoms/FormInputs";
import Loading from "@common/components/Atoms/Loading";
import Modal from "@common/components/Molecules/Modal";
import { FormField } from "@common/models";

interface ReferToAnotherJobProps {
    isOpen: boolean;
    onOkay: (jobCandidateId: number) => void;
    onCancel: () => void;
    closeModal: () => void;
    jobCandidate: {
        user_id: number;
        user_full_name: string;
    };
}

interface ReferToAnotherJobState {
    jobField: FormField;
    processing: boolean;
    loading: boolean;
    error: boolean;
    errorMessages: string[];
    selectedJob: any;
    isValid: boolean;
}

export class ReferToAnotherJob extends React.Component<
    ReferToAnotherJobProps,
    ReferToAnotherJobState
> {
    private _abortControllers: { [key: string]: AbortController } = {};

    constructor(props) {
        super(props);

        this.emptyState = this.emptyState.bind(this);
        this.getJobs = this.getJobs.bind(this);
        this.submitReferral = this.submitReferral.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.onJobChange = this.onJobChange.bind(this);

        this.state = this.emptyState();
    }

    componentWillReceiveProps(newProps) {
        if (newProps.isOpen && newProps.isOpen !== this.props.isOpen) {
            this.setState(this.emptyState());
        }
    }

    componentWillUnmount() {
        const abortControllers = this._abortControllers;
        if (Object.keys(abortControllers).length > 0) {
            Object.keys(abortControllers).map(key =>
                abortControllers[key].abort()
            );
        }
    }

    emptyState() {
        return {
            selectedJob: {} as any,
            isValid: true,
            loading: false,
            error: false,
            errorMessages: [] as string[],
            jobField: {
                name: "job",
                label: "",
                value: "",
                isValid: true,
                type: "text",
                validationMessages: [] as string[],
                rules: [Rules.required],
            },
            processing: false,
        };
    }

    getJobs(term: string) {
        // Check to see if there already was an abortController, and if so, call abort on it
        if (this._abortControllers.suggest) {
            this._abortControllers.suggest.abort();
        }
        this._abortControllers.suggest = new AbortController();
        const signal = this._abortControllers.suggest.signal;

        return JobService.suggest(term, signal)
            .then(result =>
                result.results.map(r => ({
                    label: r.item_name,
                    value: r.item_id,
                }))
            )
            .catch(err => handleError(err, this));
    }

    submitReferral() {
        const jobField = this.state.jobField;
        const isValid = FormHandler.validateField(jobField, { jobField });

        if (isValid) {
            this.setState({ jobField, processing: true }, () => {
                const jobId = this.state.jobField.value.value;
                const userId = this.props.jobCandidate.user_id;

                ReferralService.link(jobId, userId)
                    .then(jobCandidateId => {
                        this.setState({ processing: false }, () => {
                            this.props.onOkay(jobCandidateId);
                        });
                    })
                    .catch(err => handleError(err, this));
            });
        } else {
            this.setState({ jobField });
        }
    }

    onSubmit(event) {
        event.preventDefault();
        this.submitReferral();
    }

    onJobChange(selectedJobFieldName, selectedJob) {
        const jobField = this.state.jobField;
        jobField.value = selectedJob;
        this.setState({ jobField });
        this.setState({ error: false });
    }

    render() {
        if (this.props.isOpen && this.state.loading) {
            return (
                <Modal
                    title="refer to another job"
                    isOpen={this.props.isOpen}
                    closeModal={this.props.closeModal}
                >
                    <Loading />
                </Modal>
            );
        }

        return (
            <Modal
                title="refer to another job"
                isOpen={this.props.isOpen}
                closeModal={this.props.closeModal}
                loading={this.state.processing}
            >
                <div className="c-block">
                    <p>
                        What job would you like to refer{" "}
                        <strong>
                            {this.props.jobCandidate.user_full_name}
                        </strong>{" "}
                        to?
                    </p>
                </div>

                <form onSubmit={this.onSubmit}>
                    <div className="c-block">
                        <Select
                            name="job"
                            label="search jobs"
                            value={this.state.jobField.value}
                            multi={false}
                            isValid={this.state.jobField.isValid}
                            validationMessages={
                                this.state.jobField.validationMessages
                            }
                            onChange={this.onJobChange}
                            remoteOptionsMethod={this.getJobs}
                        />
                    </div>

                    <div className="c-block">
                        <Error
                            show={this.state.error}
                            description={
                                this.state.errorMessages &&
                                this.state.errorMessages.length
                                    ? this.state.errorMessages[0]
                                    : "An Error Occurred"
                            }
                        />
                    </div>

                    <div className="c-modal__footer">
                        <div className="c-modal__footer-left">
                            <Button
                                id="refer-to-another-job-refer-button"
                                green={true}
                                type="submit"
                                onClick={() => void 0}
                            >
                                refer
                            </Button>
                        </div>
                        <div className="c-modal__footer-right">
                            <Button
                                id="refer-to-another-job-cancel-button"
                                type="button"
                                onClick={this.props.onCancel}
                            >
                                cancel
                            </Button>
                        </div>
                    </div>
                </form>
            </Modal>
        );
    }
}

// Helpers...

function handleError(err, stateContainer) {
    if (err.type === "abort" || err.name === "AbortError") {
        stateContainer.setState({ loading: false, processing: false });
        return;
    }

    const messages = err.responseData
        ? err.responseData.error_messages
        : [err.message];

    console.log(messages);
    stateContainer.setState({
        loading: false,
        processing: false,
        error: true,
        errorMessages: messages,
    });
}
